// @ts-nocheck
import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2,
  Copy,
  ExternalLink,
  Trash2,
  Send,
  Mail,
  Lock,
  ChevronDown,
  ChevronRight,
  Plus,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

const CYAN = "#06B6D4";

// scope -> { label, path, eventScoped }
const SCOPE_META: Record<
  string,
  { label: string; sectionLabel: string; path: string; eventScoped: boolean; circuitScoped?: boolean }
> = {
  event: { label: "Event Sheet", sectionLabel: "EVENT SHEETS", path: "event-sheet", eventScoped: true },
  roster: { label: "Roster Status", sectionLabel: "ROSTER SHEETS", path: "roster-sheet", eventScoped: true },
  payment: { label: "Event Payments", sectionLabel: "PAYMENT SHEETS", path: "payment-sheet", eventScoped: true },
  summary: { label: "Season Summary", sectionLabel: "SEASON SUMMARY SHEETS", path: "event-summary", eventScoped: false },
  circuit: { label: "Circuit Teams", sectionLabel: "CIRCUIT SHEETS", path: "circuit-sheet", eventScoped: false, circuitScoped: true },
  players: { label: "Players", sectionLabel: "PLAYERS SHEETS", path: "players-sheet", eventScoped: true },
  checkin: { label: "Check-In", sectionLabel: "CHECK-IN SHEETS", path: "checkin-sheet", eventScoped: true },
  scheduling: { label: "Scheduling", sectionLabel: "SCHEDULING SHEETS", path: "schedule-sheet", eventScoped: true },
};

// Order for sections + create-form cards
const SCOPE_ORDER = ["event", "roster", "payment", "summary", "circuit", "players", "checkin", "scheduling"];

const PERMISSIONS = [
  { value: "view", label: "View only" },
  { value: "comment", label: "Comment" },
  { value: "edit", label: "Edit" },
];

const permBadge = (p: string) => {
  switch (p) {
    case "edit":
      return { label: "Edit", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" };
    case "comment":
      return { label: "Comment", cls: "bg-amber-100 text-amber-700 border-amber-200" };
    default:
      return { label: "View only", cls: "bg-slate-100 text-slate-600 border-slate-200" };
  }
};

const fullUrl = (scope: string, token: string) =>
  `${window.location.origin}/${SCOPE_META[scope]?.path || "event-sheet"}/${token}`;

export default function AdminLiveSheetsPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // ---- data ----
  const { data: tokens = [], isLoading } = useQuery({
    queryKey: ["admin-sheet-tokens"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("event_sheet_tokens")
        .select("*, events(name,start_date), circuits(name)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: events = [] } = useQuery({
    queryKey: ["admin-sheet-events"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("id,name,start_date")
        .order("start_date", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: circuits = [] } = useQuery({
    queryKey: ["admin-sheet-circuits"],
    queryFn: async () => {
      const { data, error } = await supabase.from("circuits").select("id,name").order("name");
      if (error) throw error;
      return data ?? [];
    },
  });

  const refetch = () => qc.invalidateQueries({ queryKey: ["admin-sheet-tokens"] });

  const grouped = useMemo(() => {
    const g: Record<string, any[]> = {};
    SCOPE_ORDER.forEach((s) => (g[s] = []));
    (tokens || []).forEach((t: any) => {
      if (!g[t.scope]) g[t.scope] = [];
      g[t.scope].push(t);
    });
    return g;
  }, [tokens]);

  // ---- token mutations ----
  const updateToken = async (id: string, patch: any) => {
    const { error } = await supabase.from("event_sheet_tokens").update(patch).eq("id", id);
    if (error) {
      toast.error(error.message);
      return false;
    }
    refetch();
    return true;
  };

  const deleteToken = async (id: string) => {
    const { error } = await supabase.from("event_sheet_tokens").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    refetch();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Live Sheets</h1>
          <p className="text-sm text-muted-foreground">
            Create and manage shareable, real-time spreadsheet links — no account needed to view.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button variant="outline" onClick={() => window.open("/my-sheets", "_blank")}>
            My Sheets Portal
          </Button>
          <Button
            style={{ backgroundColor: CYAN }}
            className="text-white hover:opacity-90"
            onClick={() => setShowForm((v) => !v)}
          >
            <Plus className="mr-1 h-4 w-4" /> New Sheet Link
          </Button>
        </div>
      </div>

      {showForm && (
        <CreateForm
          events={events}
          circuits={circuits}
          onClose={() => setShowForm(false)}
          onCreated={() => {
            setShowForm(false);
            refetch();
          }}
        />
      )}

      {/* Sections */}
      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading sheets…
        </div>
      ) : (
        <div className="space-y-4">
          {SCOPE_ORDER.map((scope) => {
            const rows = grouped[scope] || [];
            if (rows.length === 0) return null;
            const meta = SCOPE_META[scope];
            const isCollapsed = collapsed[scope];
            return (
              <div key={scope} className="rounded-lg border bg-card">
                <button
                  className="flex w-full items-center gap-2 px-4 py-3 text-left"
                  onClick={() => setCollapsed((c) => ({ ...c, [scope]: !c[scope] }))}
                >
                  {isCollapsed ? (
                    <ChevronRight className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                  <span className="text-sm font-semibold tracking-wide" style={{ color: CYAN }}>
                    {meta.sectionLabel} ({rows.length})
                  </span>
                </button>
                {!isCollapsed && (
                  <div className="divide-y border-t">
                    {rows.map((t: any) => (
                      <TokenRow
                        key={t.id}
                        token={t}
                        onUpdate={updateToken}
                        onDelete={deleteToken}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {tokens.length === 0 && (
            <p className="text-sm text-muted-foreground">No sheet links yet. Create one to get started.</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Token row                                                          */
/* ------------------------------------------------------------------ */
function TokenRow({ token, onUpdate, onDelete }: any) {
  const meta = SCOPE_META[token.scope] || {};
  const url = fullUrl(token.scope, token.token);
  const pb = permBadge(token.permission);
  const ctx =
    token.events?.name || token.circuits?.name || (token.scope === "summary" ? "Season-wide" : "—");
  const gatedCount = Array.isArray(token.allowed_emails) ? token.allowed_emails.length : 0;

  const [emailsOpen, setEmailsOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(url);
    toast.success("Copied");
  };

  return (
    <div className={`px-4 py-3 ${token.revoked ? "opacity-50" : ""}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{token.label || "—"}</span>
        <Badge variant="outline" className="border-cyan-200 bg-cyan-50 text-cyan-700">
          {ctx}
        </Badge>
        <Badge variant="outline" className={pb.cls}>
          {pb.label}
        </Badge>
        {gatedCount > 0 && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Lock className="h-3 w-3" /> {gatedCount}
          </span>
        )}
        {token.revoked && (
          <Badge variant="outline" className="border-red-200 bg-red-50 text-red-600">
            Revoked
          </Badge>
        )}
        {token.scope === "event" && (
          <label className="ml-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Checkbox
              checked={!!token.show_checkin}
              onCheckedChange={(c) => onUpdate(token.id, { show_checkin: !!c })}
            />
            Show Check-In
          </label>
        )}
      </div>

      <div className="mt-2 flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate rounded bg-muted px-2 py-1 text-xs text-muted-foreground">
          {url}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => setEmailsOpen(true)}>
          <Mail className="mr-1 h-3.5 w-3.5" /> Emails
        </Button>
        <Button size="sm" variant="outline" onClick={() => setSendOpen(true)}>
          <Send className="mr-1 h-3.5 w-3.5" /> Send
        </Button>
        <Button size="sm" variant="outline" onClick={copy}>
          <Copy className="mr-1 h-3.5 w-3.5" /> Copy
        </Button>
        <Button size="sm" variant="outline" onClick={() => window.open(url, "_blank")}>
          <ExternalLink className="mr-1 h-3.5 w-3.5" /> Open
        </Button>
        {token.revoked ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onUpdate(token.id, { revoked: false, revoked_at: null })}
          >
            Restore
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              onUpdate(token.id, { revoked: true, revoked_at: new Date().toISOString() })
            }
          >
            Revoke
          </Button>
        )}
        <Popover>
          <PopoverTrigger asChild>
            <Button size="sm" variant="outline" className="text-red-600">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56">
            <p className="mb-2 text-sm">Delete this sheet link permanently?</p>
            <Button
              size="sm"
              variant="destructive"
              className="w-full"
              onClick={() => onDelete(token.id)}
            >
              Delete
            </Button>
          </PopoverContent>
        </Popover>
      </div>

      {emailsOpen && (
        <EmailsEditor
          token={token}
          onClose={() => setEmailsOpen(false)}
          onSave={async (arr) => {
            const ok = await onUpdate(token.id, { allowed_emails: arr.length ? arr : null });
            if (ok) {
              toast.success("Saved");
              setEmailsOpen(false);
            }
          }}
        />
      )}

      {sendOpen && (
        <SendModal token={token} url={url} onClose={() => setSendOpen(false)} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Emails editor (allowed_emails chips)                                */
/* ------------------------------------------------------------------ */
function EmailsEditor({ token, onClose, onSave }: any) {
  const [emails, setEmails] = useState<string[]>(token.allowed_emails || []);
  const [input, setInput] = useState("");

  const add = () => {
    const v = input.trim().toLowerCase();
    if (v && !emails.includes(v)) setEmails([...emails, v]);
    setInput("");
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Allowed emails {emails.length ? `(${emails.length})` : "(ungated)"}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          {emails.map((e) => (
            <span
              key={e}
              className="inline-flex items-center gap-1 rounded-full bg-cyan-50 px-2 py-1 text-xs text-cyan-700"
            >
              {e}
              <button onClick={() => setEmails(emails.filter((x) => x !== e))}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {emails.length === 0 && (
            <p className="text-xs text-muted-foreground">No emails — link is public (ungated).</p>
          )}
        </div>
        <div className="flex gap-2">
          <Input
            value={input}
            placeholder="email@example.com"
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add();
              }
            }}
          />
          <Button variant="outline" onClick={add}>
            Add
          </Button>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button style={{ backgroundColor: CYAN }} className="text-white" onClick={() => onSave(emails)}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Send modal                                                          */
/* ------------------------------------------------------------------ */
function SendModal({ token, url, onClose }: any) {
  const [recipients, setRecipients] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const scopeLabel = SCOPE_META[token.scope]?.label || "sheet";
  const eventName = token.events?.name || token.circuits?.name || "";

  const send = async () => {
    const to = recipients
      .split(/[\n,]/)
      .map((e) => e.trim())
      .filter(Boolean);
    if (!to.length) return toast.error("Add at least one recipient");
    setSending(true);
    try {
      const { error } = await invokeEdgeFunction("send-system-message", {
        body: {
          organization_id: token.organization_id,
          to,
          subject: `Your ${scopeLabel} sheet: ${token.label || eventName}`,
          message: `${message}\n\nAccess your live sheet here: ${url}`,
          channel: "email",
        },
      });
      if (error) {
        toast.error(error.message || "Failed to send");
        return;
      }
      toast.success("Sent");
      onClose();
    } catch (e: any) {
      toast.error(e?.message || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send sheet link</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Recipient email(s)</Label>
            <Textarea
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder="comma or newline separated"
            />
          </div>
          <div>
            <Label>Message (optional)</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            style={{ backgroundColor: CYAN }}
            className="text-white"
            disabled={sending}
            onClick={send}
          >
            {sending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Create form                                                        */
/* ------------------------------------------------------------------ */
function CreateForm({ events, circuits, onClose, onCreated }: any) {
  const [scope, setScope] = useState("event");
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [circuitId, setCircuitId] = useState("");
  const [label, setLabel] = useState("");
  const [permission, setPermission] = useState("edit");
  const [restrict, setRestrict] = useState(false);
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [creating, setCreating] = useState(false);

  const meta = SCOPE_META[scope];
  const isEventScoped = meta.eventScoped;
  const isCircuit = scope === "circuit";

  const toggleEvent = (id: string) =>
    setSelectedEvents((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const addEmail = () => {
    const v = emailInput.trim().toLowerCase();
    if (v && !emails.includes(v)) setEmails([...emails, v]);
    setEmailInput("");
  };

  const create = async () => {
    const allowed = restrict ? emails : [];
    if (isEventScoped && selectedEvents.length === 0) return toast.error("Select at least one event");
    if (isCircuit && !circuitId) return toast.error("Select a circuit");
    setCreating(true);
    try {
      const base = {
        scope,
        permission,
        label: label || null,
        allowed_emails: allowed.length ? allowed : null,
        expires_at: expiresAt || null,
      };
      let rows: any[] = [];
      if (isEventScoped) {
        rows = selectedEvents.map((eid) => ({ ...base, event_id: eid, circuit_id: null }));
      } else if (isCircuit) {
        rows = [{ ...base, event_id: null, circuit_id: circuitId }];
      } else {
        rows = [{ ...base, event_id: null, circuit_id: null }];
      }
      const { error } = await supabase.from("event_sheet_tokens").insert(rows);
      if (error) {
        toast.error(error.message);
        return;
      }
      toast.success("Sheet link created.");
      onCreated();
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="rounded-lg border bg-card p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Create New Sheet Link</h2>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Sheet type cards */}
      <div>
        <Label className="mb-2 block">Sheet Type</Label>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {SCOPE_ORDER.map((s) => {
            const active = scope === s;
            return (
              <button
                key={s}
                onClick={() => setScope(s)}
                className={`rounded-md border px-3 py-2 text-sm transition ${
                  active ? "border-cyan-500 bg-cyan-50 text-cyan-700" : "border-input hover:bg-accent"
                }`}
              >
                {SCOPE_META[s].label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Event picker */}
      {isEventScoped && (
        <div>
          <Label className="mb-2 block">Events</Label>
          <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-2">
            {events.map((e: any) => (
              <label key={e.id} className="flex items-center gap-2 rounded px-1 py-1 text-sm hover:bg-accent">
                <Checkbox
                  checked={selectedEvents.includes(e.id)}
                  onCheckedChange={() => toggleEvent(e.id)}
                />
                <span className="flex-1">{e.name}</span>
                <span className="text-xs text-muted-foreground">{e.start_date}</span>
              </label>
            ))}
            {events.length === 0 && <p className="text-xs text-muted-foreground">No events.</p>}
          </div>
        </div>
      )}

      {/* Circuit picker */}
      {isCircuit && (
        <div>
          <Label className="mb-2 block">Circuit</Label>
          <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-2">
            {circuits.map((c: any) => (
              <label key={c.id} className="flex items-center gap-2 rounded px-1 py-1 text-sm hover:bg-accent">
                <input
                  type="radio"
                  name="circuit"
                  checked={circuitId === c.id}
                  onChange={() => setCircuitId(c.id)}
                />
                <span>{c.name}</span>
              </label>
            ))}
            {circuits.length === 0 && <p className="text-xs text-muted-foreground">No circuits.</p>}
          </div>
        </div>
      )}

      {/* Label */}
      <div>
        <Label className="mb-1 block">Label</Label>
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Venue Staff, Board Overview, Tournament Director"
        />
      </div>

      {/* Access level */}
      <div>
        <Label className="mb-2 block">Access Level</Label>
        <div className="inline-flex rounded-md border p-1">
          {PERMISSIONS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPermission(p.value)}
              className={`rounded px-3 py-1 text-sm transition ${
                permission === p.value ? "text-white" : "text-muted-foreground hover:bg-accent"
              }`}
              style={permission === p.value ? { backgroundColor: CYAN } : undefined}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Restrict emails */}
      <div>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={restrict} onCheckedChange={(c) => setRestrict(!!c)} />
          Restrict to specific emails
        </label>
        {restrict && (
          <div className="mt-2 space-y-2">
            <div className="flex flex-wrap gap-2">
              {emails.map((e) => (
                <span
                  key={e}
                  className="inline-flex items-center gap-1 rounded-full bg-cyan-50 px-2 py-1 text-xs text-cyan-700"
                >
                  {e}
                  <button onClick={() => setEmails(emails.filter((x) => x !== e))}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={emailInput}
                placeholder="email@example.com"
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addEmail();
                  }
                }}
              />
              <Button variant="outline" onClick={addEmail}>
                Add
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Expiry */}
      <div>
        <Label className="mb-1 block">Expires (optional)</Label>
        <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className="w-48" />
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          style={{ backgroundColor: CYAN }}
          className="text-white"
          disabled={creating}
          onClick={create}
        >
          {creating && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
          Create
        </Button>
      </div>
    </div>
  );
}
